import React, { useState, useEffect } from 'react';
import { FaSpinner, FaBrain, FaChartLine, FaBullseye, FaRocket } from 'react-icons/fa';

const AnalysisLoadingStep = ({ sessionId }) => {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);

    const messages = [
        {
            icon: FaBrain,
            title: 'Analizando datos del negocio',
            description: 'Recopilando información de ventas, clientes y comportamiento de compra...',
            color: 'text-blue-500'
        },
        {
            icon: FaChartLine,
            title: 'Procesando métricas clave',
            description: 'Calculando indicadores de rendimiento y tendencias de crecimiento...',
            color: 'text-green-500'
        },
        {
            icon: FaBullseye,
            title: 'Identificando oportunidades',
            description: 'Detectando áreas de mejora y potencial de crecimiento...',
            color: 'text-purple-500'
        },
        {
            icon: FaRocket,
            title: 'Generando objetivos',
            description: 'Creando objetivos específicos y medibles para tu estrategia...',
            color: 'text-orange-500'
        }
    ];

    useEffect(() => {
        // Cambiar mensaje cada 15 segundos
        const messageInterval = setInterval(() => {
            setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
        }, 15000);

        // Actualizar tiempo transcurrido cada segundo
        const timeInterval = setInterval(() => {
            setElapsedTime((prev) => prev + 1);
        }, 1000);

        return () => {
            clearInterval(messageInterval);
            clearInterval(timeInterval);
        };
    }, [messages.length]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const currentMessage = messages[currentMessageIndex];

    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="text-center">
                    {/* Spinner principal */}
                    <div className="relative mb-6">
                        <FaSpinner className="w-16 h-16 text-blue-500 animate-spin mx-auto" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 bg-white rounded-full"></div>
                        </div>
                    </div>

                    {/* Mensaje actual */}
                    <div className="mb-6">
                        <div className="flex items-center justify-center mb-4">
                            <currentMessage.icon className={`w-8 h-8 mr-3 ${currentMessage.color}`} />
                            <h3 className="text-xl font-bold text-gray-900">
                                {currentMessage.title}
                            </h3>
                        </div>
                        <p className="text-gray-600 text-lg">
                            {currentMessage.description}
                        </p>
                    </div>

                    {/* Indicadores de progreso */}
                    <div className="mb-6">
                        <div className="flex justify-center space-x-2 mb-4">
                            {messages.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-3 h-3 rounded-full transition-colors duration-500 ${index === currentMessageIndex
                                        ? 'bg-blue-500'
                                        : index < currentMessageIndex
                                            ? 'bg-green-500'
                                            : 'bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                        <p className="text-sm text-gray-500">
                            Paso {currentMessageIndex + 1} de {messages.length}
                        </p>
                    </div>

                    {/* Tiempo transcurrido */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-2">Tiempo transcurrido</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {formatTime(elapsedTime)}
                        </p>
                    </div>

                    {/* Información adicional */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                            <strong>💡 Consejo:</strong> El análisis puede tardar entre 1-2 minutos.
                            Estamos procesando una gran cantidad de datos para crear la mejor estrategia para tu negocio.
                        </p>
                    </div>

                    {/* Session ID para debug */}
                    {sessionId && (
                        <div className="mt-4 text-xs text-gray-400">
                            ID de sesión: {sessionId}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnalysisLoadingStep; 